import React, { useState } from "react";
import { Form, Input, Button, Toast, Logo, ButtonGroup, Select, TeamSearch, PasswordStrength } from "../../../../Components";
import moment from "moment";
import { FiArrowRight } from "react-icons/fi";
import { apis, post } from "../../../../Utils/axios";
import { getNameValidationError, isPasswordStrong, validatePhoneNumber } from "../../../../Utils/utils";
import { useLoginHook } from "../../../../Hooks/useLoginHook";
import { COUNTRIES } from "Utils/constants";

const MAX_DOB_DATE = moment().subtract(18, "years").endOf("day");

const formatDobInput = (value = "") => {
	const digits = value.replace(/\D/g, "").slice(0, 8);
	if (digits.length <= 2) return digits;
	if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
	return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const parseDobInput = (value = "") => {
	const parsedDob = moment(value, "DD/MM/YYYY", true);
	if (!parsedDob.isValid()) return { error: "Please enter date of birth in DD/MM/YYYY format", value: null };
	if (parsedDob.isAfter(MAX_DOB_DATE)) return { error: "Date of birth must be at least 18 years ago", value: null };
	return { error: null, value: parsedDob.valueOf() };
};

function Onboard() {
	const Params = new URLSearchParams(window.location.search);
	const code = Params.get("code");

	const { logout } = useLoginHook();
	const [SetupDone, setSetupDone] = useState();
	const [Loader, setLoader] = useState();
	const [Values, setValues] = useState({
		fName: null,
		lName: null,
		gender: {},
		phone: "",
		country: {},
		// TWCP-191: start with blank DOB so user must actively select their date of birth
		dob: null,
		team: {},
	});
	const [DobInput, setDobInput] = useState("");

	const onSubmit = async () => {
		const nameError = getNameValidationError("First name", Values.fName) || getNameValidationError("Last name", Values.lName);
		if (nameError) {
			return Toast.error(nameError);
		}
		if (!Values.gender?.value) {
			return Toast.error("Please select gender");
		}
		if (!Values.team?.value) {
			return Toast.error("Please select team");
		}
		if (!DobInput) {
			return Toast.error("Please enter date of birth");
		}
		const parsedDob = parseDobInput(DobInput);
		if (parsedDob.error) {
			return Toast.error(parsedDob.error);
		}
		if (Values.password !== Values.confirm) {
			Toast.error("Passwords do not match");
			return;
		}
		if (!isPasswordStrong(Values.password)) {
			Toast.error(
				"Password must be at least 6 characters and include uppercase, lowercase, a number, and one special character (e.g. ! @ # $ % ^ & * _ - +)."
			);
			return;
		}
		setLoader(true);
		if (!(await validatePhoneNumber(Values.phone, Values.country.value))) {
			setLoader();
			return Toast.error("Please enter a valid phone number");
		}
		post(
			apis.onboardNewUser,
			{
				...Values,
				dob: moment(parsedDob.value).toDate(),
				gender: Values.gender?.value,
				phoneCode: Values.country?.value,
				code,
				teamId: Values.team?.value,
			},
			(r, e) => {
				if (r) {
					setSetupDone(true);
					setLoader();
				} else if (e) {
					Toast.handleError(e);
					setLoader();
				}
			}
		);
	};

	if (SetupDone) {
		return (
			<div className="flex items-center justify-center min-h-screen px-4 py-8">
				<div className="w-full max-w-md p-3 md:p-4 text-center">
					<img src="https://cdn.travelwings.com/assets/corp/images/thanks.jpg" alt="Happy dog" className="max-w-xs m-auto my-8 rounded-full" />
					<Logo className="justify-center" />
					<div className="my-6 text-5xl font-bold">Account Ready</div>
					<div className="my-3 text-sm">Your password has been successfully activated, you can now sign in</div>
					<Button onClick={logout} className="m-auto my-8">
						Sign in to your account
					</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="w-full min-h-screen overflow-y-auto">
				<div className="w-full max-w-xl px-4 pt-3 pb-24 mx-auto text-center sm:pt-6 sm:pb-32">
					<div className="flex justify-center">
						<Logo className="justify-center" />
					</div>
					<div className="inline-flex px-3 py-1 mt-3 text-xs font-semibold tracking-wide uppercase rounded-full bg-white/10 text-white/80">
						Travelwings for Business
					</div>
					<div className="mt-3 text-2xl font-bold leading-tight sm:mt-4 sm:text-5xl">Complete your profile to activate your account</div>
					<div className="max-w-lg mx-auto mt-2 text-sm leading-6 text-white/80 sm:mt-3 sm:text-base">
						Set your password and finish your profile so you can start booking business travel. Enter your date of birth in DD/MM/YYYY format. You must be at least 18 years old.
					</div>

					<div className="flex flex-col max-w-sm p-4 mx-auto mt-5 border text-start rounded-2xl border-white/10 bg-white/5 backdrop-blur-sm sm:mt-8 sm:p-6">
						<Form onSubmit={onSubmit} loading={Loader}>
							<div className="grid gap-4 mb-4 lg:grid-cols-2">
								<Input noNumbers autocomplete="new-password" required value={Values.fName} onChange={(fName) => setValues((prev) => ({ ...prev, fName }))} label="First Name" placeholder="First Name" />
								<Input noNumbers autocomplete="new-password" required value={Values.lName} onChange={(lName) => setValues((prev) => ({ ...prev, lName }))} label="Last Name" placeholder="Last Name" />
							</div>
							<div className="grid gap-4 mb-4 lg:grid-cols-2">
								<div>
									<Input
										autocomplete="new-password"
										type="password"
										value={Values.password}
										onChange={(password) => setValues((prev) => ({ ...prev, password }))}
										required
										label="New Password"
										placeholder="Password"
										allowSpecialChars
									/>
									<PasswordStrength password={Values.password} />
								</div>
								<Input
									autocomplete="new-password"
									type="password"
									value={Values.confirm}
									onChange={(confirm) => setValues((prev) => ({ ...prev, confirm }))}
									required
									label="Confirm New Password"
									placeholder="Confirm Password"
									allowSpecialChars
								/>
							</div>
							<div className="grid gap-4 mb-4 lg:grid-cols-2">
								<div>
									<label className="block mb-2">Gender</label>
									<ButtonGroup
										selected={Values.gender}
										onClick={(gender) => setValues((prev) => ({ ...prev, gender }))}
										options={[
											{
												label: "Male",
												value: "MALE",
											},
											{
												label: "Female",
												value: "FEMALE",
											},
										]}
									/>
								</div>
								<Input
									autocomplete="bday"
									value={DobInput}
									onChange={(value) => {
										const formattedDob = formatDobInput(value);
										const parsedDob = parseDobInput(formattedDob);
										setDobInput(formattedDob);
										setValues((prev) => ({
											...prev,
											dob: parsedDob.value,
										}));
									}}
									required
									label="Date of Birth"
									placeholder="DD/MM/YYYY"
									maxLength={10}
									allowSpecialChars
									inputClass="tracking-wide"
								/>
								<div className="mt-1 text-xs leading-5 text-secondary">Use `DD/MM/YYYY`, for example `11/03/2008`.</div>
							</div>
							<label>Phone Number</label>
							<div className="flex gap-2">
								<Select className="max-w-min" placeholder="Code" selected={Values.country} onChange={(country) => setValues((prev) => ({ ...prev, country }))} required label="" options={COUNTRIES} />
								<Input autocomplete="new-password" className="flex-1" type="number" required value={Values.phone} onChange={(phone) => setValues((prev) => ({ ...prev, phone }))} placeholder="Phone" />
							</div>
							<div className="pb-24 sm:pb-28">
								<TeamSearch authCode={code} searchOnly className="mt-2" label="Select your team" placeholder="Search Teams" onSelect={(team) => setValues((prev) => ({ ...prev, team }))} selected={Values.team} />
							</div>
							<Button variant="primary" loading={Loader} type="submit" className="w-full mt-2 sm:mt-4">
								Go to your Account <FiArrowRight className="ml-2" />
							</Button>
						</Form>
					</div>
				</div>
			</div>
		</>
	);
}

export default Onboard;
